import React, { useCallback } from 'react';
import { AutoAwesome, HelpOutline, Shield, ShieldOutlined } from '@mui/icons-material';
import {
    DEFENSE_SHARE_MAX,
    DEFENSE_SHARE_STEP,
    normalizeDefenseShare,
} from '../../core/GenConfig';
import { useOverlay } from '../../contexts/OverlayContext';
import SliderButton from '../utils/SliderButton';
import './DefenseShareControl.css';

const TXT_TITLE = { fr: 'Défense automatique', en: 'Automatic defense', de: 'Automatische Verteidigung', es: 'Defensa automática', pt: 'Defesa automática' };
const TXT_SHEET_LABEL = { fr: 'Défense auto', en: 'Auto defense', de: 'Auto-Verteidigung', es: 'Defensa auto', pt: 'Defesa auto' };
const TXT_DEFENSE = { fr: 'Défense', en: 'Defense', de: 'Verteidigung', es: 'Defensa', pt: 'Defesa' };
const TXT_CRITERIA = { fr: 'Critères', en: 'Criteria', de: 'Kriterien', es: 'Criterios', pt: 'Critérios' };
const TXT_SUBTITLE = {
    fr: 'Attribue automatiquement une part du score aux stats défensives (PV, résistances), et le reste à vos critères. Par défaut : 0 % = inactif.',
    en: 'Automatically gives a share of the score to defensive stats (HP, resistances), and the rest to your criteria. Default: 0% = off.',
    de: 'Weist automatisch einen Teil des Scores defensiven Stats (LP, Resistenzen) zu, den Rest deinen Kriterien. Standard: 0 % = aus.',
    es: 'Asigna automáticamente una parte de la puntuación a las stats defensivas (PdV, resistencias), y el resto a tus criterios. Por defecto: 0 % = desactivado.',
    pt: 'Atribui automaticamente uma parte da pontuação às stats defensivas (PV, resistências), e o resto aos seus critérios. Padrão: 0 % = desativado.',
};
const PERCENT_SCALE = 100;
const PERCENT_MIN = 0;
const PERCENT_TOTAL = 100;

const pct = (share) => Math.round(normalizeDefenseShare(share) * PERCENT_SCALE);

const DefenseShareControl = ({
    lang = 'fr',
    defenseShare = 0,
    onDefenseShareChange,
    disabled = false,
    variant = 'pill',
}) => {
    const { showOverlay } = useOverlay();
    const share = normalizeDefenseShare(defenseShare);
    const value = pct(share);
    const criteriaValue = PERCENT_TOTAL - value;
    const isOn = share > 0;
    const isSheet = variant === 'sheet';
    const title = TXT_TITLE[lang] || TXT_TITLE.en;
    const sliderLabel = `${title}: ${value}% / ${criteriaValue}%`;
    const subtitle = TXT_SUBTITLE[lang] || TXT_SUBTITLE.en;
    const defenseLabel = TXT_DEFENSE[lang] || TXT_DEFENSE.en;
    const criteriaLabel = TXT_CRITERIA[lang] || TXT_CRITERIA.en;
    const tooltip = (
        <div className={`defense-share-tooltip${isSheet ? ' defense-share-tooltip--sheet' : ''}`}>
            <div className="defense-share-tooltip__header">
                <h6 className="defense-share-tooltip__title">{title}</h6>
                <span className="defense-share-tooltip__subtitle">{subtitle}</span>
            </div>
            <div className="defense-share-tooltip__split">
                <div className="defense-share-tooltip__track" aria-hidden="true">
                    <span
                        className={`defense-share-tooltip__track-defense${value === 0 ? ' defense-share-tooltip__track-defense--zero' : ''}`}
                        style={value > 0 ? { width: `${value}%` } : undefined}
                    />
                    <span
                        className="defense-share-tooltip__track-criteria"
                        style={{ width: `${criteriaValue}%` }}
                    />
                </div>
                <div className="defense-share-tooltip__legend">
                    <span className="defense-share-tooltip__legend-item defense-share-tooltip__legend-item--defense">
                        <Shield fontSize="small" />
                        <strong>{value}%</strong>
                        <span>{defenseLabel}</span>
                    </span>
                    <span className="defense-share-tooltip__legend-item defense-share-tooltip__legend-item--criteria">
                        <span>{criteriaLabel}</span>
                        <strong>{criteriaValue}%</strong>
                        <AutoAwesome fontSize="small" />
                    </span>
                </div>
            </div>
        </div>
    );
    const showInfo = () => {
        showOverlay(tooltip, { compact: true });
    };

    const handleChange = useCallback((nextValue) => {
        if (disabled) return;
        onDefenseShareChange?.(normalizeDefenseShare(nextValue / PERCENT_SCALE));
    }, [disabled, onDefenseShareChange]);

    const shieldIcon = isOn ? (
        <Shield className="defense-share-control__icon" fontSize="small" />
    ) : (
        <ShieldOutlined className="defense-share-control__icon" fontSize="small" />
    );

    const slider = (
        <SliderButton
            value={value}
            onChange={handleChange}
            min={PERCENT_MIN}
            max={Math.round(DEFENSE_SHARE_MAX * PERCENT_SCALE)}
            step={Math.round(DEFENSE_SHARE_STEP * PERCENT_SCALE)}
            disabled={disabled}
            label={sliderLabel}
            tooltipContent={isSheet ? null : tooltip}
            visualMin={PERCENT_MIN}
            visualMax={isSheet ? PERCENT_TOTAL : undefined}
            className={`defense-share-control${isOn ? ' defense-share-control--on' : ''}${value === 0 ? ' defense-share-control--zero' : ''}${isSheet ? ' defense-share-control--sheet' : ''}`}
        >
            {isSheet ? (
                <>
                    <span className="defense-share-control__side defense-share-control__side--defense">
                        <Shield className="defense-share-control__side-icon" fontSize="small" />
                        <span className="defense-share-control__value">{value}%</span>
                    </span>
                    <span className="defense-share-control__side defense-share-control__side--criteria">
                        <span className="defense-share-control__value">{criteriaValue}%</span>
                        <AutoAwesome className="defense-share-control__side-icon" fontSize="small" />
                    </span>
                </>
            ) : (
                <>
                    {shieldIcon}
                    <span className="defense-share-control__value">{value}%</span>
                </>
            )}
        </SliderButton>
    );

    if (isSheet) {
        const sheetLabel = TXT_SHEET_LABEL[lang] || TXT_SHEET_LABEL.en;
        return (
            <div className={`defense-share-sheet-row row${isOn ? ' defense-share-sheet-row--on' : ''}`}>
                <span className="mobile-sheet-list__icon-slot">
                    {shieldIcon}
                </span>
                <span className="mobile-sheet-list__label">{sheetLabel}</span>
                <button
                    type="button"
                    className="defense-share-sheet-row__help"
                    onClick={showInfo}
                    aria-label={title}
                >
                    <HelpOutline fontSize="small" />
                </button>
                <div className="defense-share-sheet-row__slider">
                    {slider}
                </div>
            </div>
        );
    }

    return slider;
};

export default DefenseShareControl;
